import { DateTime } from "luxon";

import { LISSpecimen } from "pwapi";

import { numberName } from "shared/utils/numberName";

import {
  isEllipse,
  isExcisionWithTips,
  isMultipleExcisions,
  isMultiplePunches,
  isMultipleShaves,
  isOrientedEllipse,
  isShaveFragments,
} from "./conditions";
import { GrossingSelections } from "./selections";

// GrossingTokens are represented in GrossingSegments with the syntax "{token}", eg: "{medium}" or "{measurement_w}".
//
// token                - Must match it's key in the GrossingTokens definitions.
// options              - An array of options for the User to select.
// allowCustom?         - Enables the option to type a custom value.
//                        Specific input types "number", "big", or "integer" may be specified.
// customMin?           - Minimum value when allowCustom specifies a numeric input.
// step?                - Increment value when allowCustom specifies a numeric input.
// defaultCustomValue?  - Default value... but this should be getting set on the backend. Possibly needs to be deprecated?
// conditionalOutput?   - Transforms the output of a token using GrossingSelections.
// allowMultiple?       - Allows the token to be used multiple times with different values instead of one shared value.
//                        When set to "autofill" empty repeated tokens will default to the first value selected.

export type GrossingToken = {
  token: string;
  options: string[];
  allowCustom?:
    | boolean
    | "number"
    | "big"
    | "integer"
    | (({
        selections,
      }: {
        selections: GrossingSelections;
      }) => boolean | "integer");
  customMin?: number;
  step?: number;
  defaultCustomValue?:
    | React.HTMLAttributes<HTMLInputElement>["defaultValue"]
    | (() => React.HTMLAttributes<HTMLInputElement>["defaultValue"]);
  conditionalOutput?: (value: string, selections: GrossingSelections) => string;
  allowMultiple?: boolean | "autofill";
};

const cmOptions = [
  "0.1",
  "0.2",
  "0.3",
  "0.4",
  "0.5",
  "0.6",
  "0.7",
  "0.8",
  "0.9",
  "1.0",
  "1.1",
  "1.2",
  "1.3",
  "1.4",
  "1.5",
];

const clockOptions = [
  "1:00",
  "2:00",
  "3:00",
  "4:00",
  "5:00",
  "6:00",
  "7:00",
  "8:00",
  "9:00",
  "10:00",
  "11:00",
  "12:00",
];

const orientingColorOptions = [
  "blue",
  "green",
  "black",
  "red",
  "yellow",
  "orange",
];

const digitOptions = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

const orientationOptions = [
  "notch",
  "score",
  "suture",
  "short suture",
  "long suture",
];

export const grossingTokens: {
  [key: string]: GrossingToken;
} = {
  medium: {
    token: "medium",
    options: ["formalin", "Michel's medium"],
    allowCustom: true,
  },
  measurement_w: {
    token: "measurement_w",
    options: cmOptions,
    allowCustom: "number",
    customMin: 1.6,
    step: 0.1,
    defaultCustomValue: "1.6",
    allowMultiple: true,
  },
  measurement_l: {
    token: "measurement_l",
    options: cmOptions,
    allowCustom: "number",
    customMin: 1.6,
    step: 0.1,
    defaultCustomValue: "1.6",
    allowMultiple: true,
  },
  measurement_h: {
    token: "measurement_h",
    options: cmOptions,
    allowCustom: "number",
    customMin: 1.6,
    step: 0.1,
    defaultCustomValue: "1.6",
    allowMultiple: true,
  },
  measurement_d: {
    token: "measurement_d",
    options: cmOptions,
    allowCustom: "number",
    customMin: 1.6,
    step: 0.1,
    defaultCustomValue: "1.6",
    allowMultiple: true,
  },
  cyst_measurement: {
    token: "cyst_measurement",
    options: cmOptions,
    allowCustom: "number",
    customMin: 1.6,
    step: 0.1,
    defaultCustomValue: "1.6",
  },
  type: {
    // this is a misleading token name because it's value comes from Specimen.procedure
    token: "type",
    options: ["punch", "shave", "excision"],
    allowCustom: ({ selections }) =>
      selections.specimen?.procedure === LISSpecimen.procedure.O,
    conditionalOutput: (value, s) =>
      isOrientedEllipse(s)
        ? "oriented ellipse"
        : isEllipse(s)
        ? "ellipse"
        : isMultipleShaves(s)
        ? "shaves"
        : isMultipleExcisions(s)
        ? "excisions"
        : isMultiplePunches(s)
        ? "punches"
        : value,
  },
  ink: {
    token: "ink",
    options: ["inked", "not inked"],
  },
  base_ink_color: {
    token: "base_ink_color",
    options: ["yellow", "orange", "green", "blue", "black", "red"],
  },
  grossed: {
    token: "grossed",
    options: ["bisected", "trisected", "serially sectioned", ""],
    allowMultiple: true,
  },
  submission: {
    token: "submission",
    options: ["entirely", "representatively"],
    allowMultiple: true,
  },
  cassette_count: {
    token: "cassette_count",
    options: digitOptions.slice(1),
    conditionalOutput: (value, s) => {
      const { specimen } = s;

      if (
        isMultipleExcisions(s) ||
        isMultipleShaves(s) ||
        isMultiplePunches(s) ||
        isShaveFragments(s)
      ) {
        return value === "1"
          ? `${specimen.name}`
          : `${specimen.name}1 - ${specimen.name}${value}`;
      }
      if (isExcisionWithTips(s)) {
        return value === "2"
          ? `${specimen.name}${value}`
          : value === "3"
          ? `${specimen.name}2 and ${specimen.name}${value}`
          : `${specimen.name}2 through ${specimen.name}${value}`;
      }
      return value === "1"
        ? `${specimen.name}`
        : value === "2"
        ? `${specimen.name}1 and ${specimen.name}${value}`
        : `${specimen.name}1 through ${specimen.name}${value}`;
    },
    allowCustom: "integer",
    customMin: 10,
    defaultCustomValue: "10",
    step: 1,
  },
  type_count: {
    token: "type_count",
    options: digitOptions.slice(1),
    conditionalOutput: (value) => {
      return numberName(value);
    },
    allowCustom: "integer",
    customMin: 10,
    defaultCustomValue: "10",
    step: 1,
  },
  type_cassette_count: {
    token: "type_cassette_count",
    options: digitOptions.slice(1),
    conditionalOutput: (value, s) => {
      // If a previous type_cassette_count exists, use it to determine the range
      const typeCassetteCounts = Object.keys(s.tokenSelections)
        .filter((key) => key.startsWith("type_cassette_count"))
        .map((key) => s.tokenSelections[key]);
      const currentIndex = typeCassetteCounts.findIndex(
        (key) => key.value === value
      );
      const previousTypeCassette = typeCassetteCounts[currentIndex - 1];
      const nextAfterPreviousValue = previousTypeCassette?.value
        ? (parseInt(previousTypeCassette.value) + 1).toString()
        : "1";

      const { specimen } = s;

      return value === nextAfterPreviousValue
        ? `${specimen.name}${value}`
        : `${specimen.name}${nextAfterPreviousValue} - ${specimen.name}${value}`;
    },
    allowMultiple: true,
    customMin: 10,
    step: 1,
  },
  source: {
    token: "source",
    options: [],
    allowCustom: true,
  },
  withA: {
    token: "withA",
    options: [],
    allowCustom: "big",
    allowMultiple: true,
  },
  // There are a lot of tokens required for the Oriented Ellipse modifier.
  // At this point it would be nice to come up with a more easily re-usable
  // approach to defining these tokens.
  orienting_mark: {
    token: "orienting_mark",
    options: orientationOptions,
    allowCustom: true,
  },
  additional_orienting_mark: {
    token: "additional_orienting_mark",
    options: orientationOptions,
    allowCustom: true,
  },
  orientation_margin: {
    token: "orientation_margin",
    options: clockOptions,
    allowCustom: "number",
  },
  additional_orientation_margin: {
    token: "additional_orientation_margin",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_blue_a: {
    token: "orientation_blue_a",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_blue_b: {
    token: "orientation_blue_b",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_color_blue: {
    token: "orientation_color_blue",
    options: orientingColorOptions,
  },
  orientation_black_a: {
    token: "orientation_black_a",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_black_b: {
    token: "orientation_black_b",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_color_black: {
    token: "orientation_color_black",
    options: orientingColorOptions,
  },
  orientation_green_a: {
    token: "orientation_green_a",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_green_b: {
    token: "orientation_green_b",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_color_green: {
    token: "orientation_color_green",
    options: orientingColorOptions,
  },
  orientation_red_a: {
    token: "orientation_red_a",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_red_b: {
    token: "orientation_red_b",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_color_red: {
    token: "orientation_color_red",
    options: orientingColorOptions,
  },
  orientation_yellow_a: {
    token: "orientation_yellow_a",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_yellow_b: {
    token: "orientation_yellow_b",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_color_yellow: {
    token: "orientation_color_yellow",
    options: orientingColorOptions,
  },
  orientation_submission_a: {
    token: "orientation_submission_a",
    options: clockOptions,
    allowCustom: "number",
  },
  orientation_submission_b: {
    token: "orientation_submission_b",
    options: clockOptions,
    allowCustom: "number",
  },
  clipping_count: {
    token: "clipping_count",
    options: ["1", "2", "3", "4", "5", "6", "7", "8", "9"],
    allowCustom: "number",
  },
  sectioned_a: {
    token: "sectioned_a",
    options: clockOptions,
    allowCustom: "number",
    allowMultiple: true,
  },
  sectioned_b: {
    token: "sectioned_b",
    options: clockOptions,
    allowCustom: "number",
    allowMultiple: true,
  },
  inked_color: {
    token: "inked_color",
    options: ["green", "blue", "red", "yellow", "black", "orange"],
    allowMultiple: true,
  },
  lipoma_medium: {
    token: "lipoma_medium",
    options: ["in aggregate", ""],
  },
  slow_mohs_shape: {
    token: "slow_mohs_shape",
    options: [
      "annular",
      "circular",
      "o'shaped",
      "debulk",
      "circumferential margin",
    ],
  },
  slow_mohs_orientation: {
    token: "slow_mohs_orientation",
    options: ["short suture", "long suture", "notch", "score"],
    allowMultiple: true,
  },
  slow_mohs_margin: {
    token: "slow_mohs_margin",
    options: clockOptions,
    allowCustom: "number",
    allowMultiple: true,
  },
  slow_mohs_orienting_margin: {
    token: "slow_mohs_orienting_margin",
    options: clockOptions,
    allowCustom: "number",
    allowMultiple: true,
  },
  slow_mohs_margin_number: {
    token: "slow_mohs_margin_number",
    options: [
      "first",
      "second",
      "third",
      "fourth",
      "fifth",
      "sixth",
      "seventh",
      "eigth",
      "ninth",
    ],
    allowMultiple: true,
  },
  slow_mohs_margin_label: {
    token: "slow_mohs_margin_label",
    options: ["quadrant", "strip"],
    allowMultiple: true,
  },
  empty_datetime: {
    token: "empty_datetime",
    options: [],
    allowCustom: true,
    defaultCustomValue: () => DateTime.now().toFormat("M/d/yyyy 'at' h:mm a"),
  },
  fm_margin_a: {
    token: "fm_margin_a",
    options: clockOptions,
    allowCustom: "number",
  },
  fm_margin_b: {
    token: "fm_margin_b",
    options: clockOptions,
    allowCustom: "number",
  },
  fm_grossed: {
    token: "fm_grossed",
    options: ["bisected", "trisected", "serially sectioned"],
  },
  fm_color: {
    token: "fm_color",
    options: orientingColorOptions,
  },
  fm_cassette_choice: {
    token: "fm_cassette_choice",
    options: digitOptions,
    conditionalOutput: (value, s) => {
      const { specimen } = s;
      return value === "1" &&
        parseInt(s.tokenSelections.cassette_count?.value || "1") === 1
        ? `${specimen.name}`
        : `${specimen.name}${value}`;
    },
  },
  fm_cassette_range: {
    token: "fm_cassette_range",
    options: digitOptions,
    conditionalOutput: (value, s) => {
      if (!value) return "";
      const { specimen } = s;
      const startIntValue = parseInt(
        s.tokenSelections.fm_cassette_choice?.value || "1"
      );
      const intValue = parseInt(value || "1");
      if (intValue - startIntValue === 1)
        return ` and ${specimen.name}${value}`;
      return ` through ${specimen.name}${value}`;
    },
  },
  fb_text: {
    token: "fb_text",
    options: [],
    allowCustom: true,
  },
  fb_w: {
    token: "fb_w",
    options: cmOptions,
    allowCustom: "number",
    customMin: 1.6,
    step: 0.1,
    defaultCustomValue: "1.6",
  },
  fb_l: {
    token: "fb_l",
    options: cmOptions,
    allowCustom: "number",
    customMin: 1.6,
    step: 0.1,
    defaultCustomValue: "1.6",
  },
  fb_h: {
    token: "fb_h",
    options: cmOptions,
    allowCustom: "number",
    customMin: 1.6,
    step: 0.1,
    defaultCustomValue: "1.6",
  },
  ew_epidermis_cassette_choice: {
    token: "ew_epidermis_cassette_choice",
    options: digitOptions,
    conditionalOutput: (value, s) => {
      const { specimen } = s;
      return value === "1" &&
        parseInt(s.tokenSelections.cassette_count?.value || "1") === 1
        ? `${specimen.name}`
        : `${specimen.name}${value}`;
    },
    allowMultiple: true,
  },
  ew_conjunctiva_cassette_choice: {
    token: "ew_conjunctiva_cassette_choice",
    options: digitOptions,
    conditionalOutput: (value, s) => {
      const { specimen } = s;
      return value === "1" &&
        parseInt(s.tokenSelections.cassette_count?.value || "1") === 1
        ? `${specimen.name}`
        : `${specimen.name}${value}`;
    },
    allowMultiple: true,
  },
  ew_medial_color: {
    token: "ew_medial_color",
    options: orientingColorOptions,
  },
  ew_lateral_color: {
    token: "ew_lateral_color",
    options: orientingColorOptions,
  },
  ew_false_color: {
    token: "ew_false_color",
    options: orientingColorOptions,
  },
};
